import React, { useState, useEffect } from 'react';
import { Alert, Form, Button, Card, FilterGrid } from '@/components/ui';
import reportService, { CattleguardPreviewData } from '../../../services/reportService';
import ReportParametersCard from '../components/ReportParametersCard';
import ReportPreviewTable from '../components/ReportPreviewTable';
import ExportOptionsModal from '../components/ExportOptionsModal';
import Toast from '../../../components/Toast';
import YellowHeaderInfo from '../components/YellowHeaderInfo';
import { ServiceArea } from '../../../services/areasService';

interface CattleguardReportViewProps {
  serviceAreas: ServiceArea[];
  canCreateCattleguard: boolean;
  loadingServiceAreas: boolean;
  defaultServiceAreaId?: string;
}

const CattleguardReportView: React.FC<CattleguardReportViewProps> = ({ 
  serviceAreas, 
  canCreateCattleguard, 
  loadingServiceAreas
}) => {
  const [filters, setFilters] = useState({
    serviceArea: '',
    startDate: '',
    endDate: ''
  });
  const [previewData, setPreviewData] = useState<CattleguardPreviewData[]>([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 50,
    total: 0,
    totalPages: 0,
    onPageChange: (page: number, pageSize?: number) => handleGeneratePreview(page, pageSize)
  });
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [loadingExport, setLoadingExport] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [toastMessage, setToastMessage] = useState<string>('');
  const [showToast, setShowToast] = useState(false);
  const [sortField, setSortField] = useState<string>('');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [isFiltersCollapsed, setIsFiltersCollapsed] = useState(false);

  const showSuccessToast = (message: string) => {
    setToastMessage(message);
    setShowToast(true);
  };

  // Clear data when service area changes (but don't auto-generate)
  useEffect(() => {
    if (filters.serviceArea) {
      // Clear existing data first to prevent showing wrong service area data
      setPreviewData([]);
      setPagination({
        page: 1,
        limit: 50,
        total: 0,
        totalPages: 0,
        onPageChange: (page: number, pageSize?: number) => handleGeneratePreview(page, pageSize)
      });
      // Don't auto-generate - user must click Generate button
    }
  }, [filters.serviceArea]); // Only depend on serviceArea, not on previewData to avoid infinite loops

  const handleGeneratePreview = async (page: number = 1, pageSize?: number) => {
    setLoadingPreview(true);
    setErrorMessage(null);
    try {
      const response = await reportService.getCattleguardPreviewData({
        serviceArea: filters.serviceArea,
        startDate: filters.startDate,
        endDate: filters.endDate,
        page,
        limit: pageSize || pagination.limit,
        sortField,
        sortDirection
      });

      setPreviewData(response.data);
      setPagination({
        ...response.pagination,
        onPageChange: (page: number, pageSize?: number) => handleGeneratePreview(page, pageSize)
      });
      
    } catch (error) {
      setErrorMessage('Failed to generate cattleguard report preview. Please try again.');
    } finally {
      setLoadingPreview(false);
    }
  };

  const handleSort = (field: string) => {
    // Determine sort direction: if same field, toggle direction; if new field, default to desc
    const newDirection = field === sortField && sortDirection === 'desc' ? 'asc' : 'desc';
    setSortField(field);
    setSortDirection(newDirection);
    handleGeneratePreview(1); // Reset to first page when sorting
  };

  const handleExport = async (customFilename?: string) => {
    setLoadingExport(true);
    try {
      await reportService.generateCattleguardReport({
        serviceArea: filters.serviceArea,
        startDate: filters.startDate,
        endDate: filters.endDate,
        customFilename
      });

      showSuccessToast('Cattleguard report exported successfully!');
      setShowExportModal(false);
      
    } catch (error) {
      setErrorMessage('Failed to export cattleguard report. Please try again.');
    } finally {
      setLoadingExport(false);
    }
  };

  // Generate default filename based on format: Cattleguards - MM/DD/YYYY
  const generateDefaultFilename = () => {
    if (filters.endDate) {
      const date = new Date(filters.endDate);
      const dateStr = `${(date.getMonth() + 1).toString().padStart(2, '0')}/${date.getDate().toString().padStart(2, '0')}/${date.getFullYear()}`;
      return `Cattleguards - ${dateStr}`;
    }
    // Fallback to current date
    const date = new Date();
    const dateStr = `${(date.getMonth() + 1).toString().padStart(2, '0')}/${date.getDate().toString().padStart(2, '0')}/${date.getFullYear()}`;
    return `Cattleguards - ${dateStr}`;
  };

  const handleExportWithFilename = async (postedDate: string, customFilename?: string) => {
    setLoadingExport(true);
    try {
      await reportService.generateCattleguardReport({
        serviceArea: filters.serviceArea,
        startDate: filters.startDate,
        endDate: filters.endDate,
        customFilename
      });

      showSuccessToast('Cattleguard report exported successfully!');
      setShowExportModal(false);
      
    } catch (error) {
      setErrorMessage('Failed to export cattleguard report. Please try again.');
    } finally {
      setLoadingExport(false);
    }
  };

  const columns = [
    { 
      label: 'Employee Name', 
      key: 'employeeName',
      sortable: false,
      width: '160px',
      headerTooltip: 'Yellow header columns are informational only and will not be included in the CSV export',
      render: (item: CattleguardPreviewData) => item.employeeName || item.formIdEmployee
    },
    { 
      label: 'Inventory Type', 
      key: 'inventoryType',
      sortable: false,
      width: '130px'
    },
    { 
      label: 'RFI Route', 
      key: 'rfiRoute',
      sortable: false,
      width: '120px'
    },
    { 
      label: 'Start Chainage', 
      key: 'startChainage',
      sortable: false,
      width: '130px'
    },
    { 
      label: 'End Chainage', 
      key: 'endChainage',
      sortable: false,
      width: '130px'
    },
    { 
      label: 'Admin Unit', 
      key: 'adminUnit',
      sortable: true,
      width: '120px'
    },
    { 
      label: 'Start Date', 
      key: 'startDate',
      sortable: true,
      width: '120px'
    },
    { 
      label: 'XSP', 
      key: 'xps',
      sortable: false,
      width: '80px'
    },
    { 
      label: 'Cattle Guard Type', 
      key: 'cattleGuardType',
      sortable: false,
      width: '150px'
    },
    { 
      label: 'Cattle Guard Width', 
      key: 'cattleGuardWidth',
      sortable: false,
      width: '160px'
    },
    { 
      label: 'Cattle Guard Length', 
      key: 'cattleGuardLength',
      sortable: false,
      width: '160px'
    },
    { 
      label: 'Running Strips', 
      key: 'runningStrips',
      sortable: false,
      width: '130px'
    },
    { 
      label: 'Cattle Guard Base', 
      key: 'cattleGuardBase',
      sortable: false,
      width: '150px'
    },
    { 
      label: 'Fish Sensitive', 
      key: 'fishSensitive',
      sortable: false,
      width: '130px'
    },
    { 
      label: 'Fish Passage', 
      key: 'fishPassage',
      sortable: false,
      width: '130px'
    },
    { 
      label: 'Grate', 
      key: 'grate',
      sortable: false,
      width: '90px'
    },
    { 
      label: 'Flap Grate', 
      key: 'flapGrate',
      sortable: false,
      width: '110px'
    },
    { 
      label: 'Installation Date', 
      key: 'installationDate',
      sortable: false,
      width: '140px'
    },
    { 
      label: 'Comments', 
      key: 'comments',
      sortable: false,
      width: '200px'
    },
    { 
      label: 'X_Coord', 
      key: 'xCoord',
      sortable: false,
      width: '100px'
    },
    { 
      label: 'Y_Coord', 
      key: 'yCoord',
      sortable: false,
      width: '100px'
    }
  ];

  return (
    <div style={{ 
      display: 'flex', 
      flexDirection: 'column'
    }}>
      {/* Header Section - Fixed */}
      <div style={{ flexShrink: 0 }}>
        {errorMessage && <Alert variant="danger" className="tw-mb-3">{errorMessage}</Alert>}
        
      <ReportParametersCard
        onGenerate={() => handleGeneratePreview(1)}
        onExport={() => {}} // Remove export functionality from top
        loadingGenerate={loadingPreview}
        loadingExport={false}
        disableExport={true} // Disable export button in top bar
        generateButtonText="Run Report"
        hideExportButton={true} // Hide the export button entirely
        defaultCollapsed={false}
        onCollapseChange={setIsFiltersCollapsed} // Track collapse state
      >
        <FilterGrid>
          <Form.Group className="tw-mb-1">
            <Form.Label className="tw-text-sm tw-mb-1">Service Area</Form.Label>
            <Form.Select
              size="sm"
              value={filters.serviceArea}
              onChange={(e) => setFilters({ ...filters, serviceArea: e.target.value })}
              disabled={loadingServiceAreas}
            >
              <option value="">All Service Areas</option>
              {serviceAreas.map((area) => (
                <option key={area.id} value={area.id}>
                  {area.name}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
          <Form.Group className="tw-mb-1">
            <Form.Label className="tw-text-sm tw-mb-1">Date Range</Form.Label>
            <div className="tw-flex tw-items-center tw-gap-2">
              <Form.Control
                size="sm"
                type="date"
                value={filters.startDate}
                onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
                style={{ width: '140px' }}
              />
              <span className="tw-text-content-muted tw-text-sm">to</span>
              <Form.Control
                size="sm"
                type="date"
                value={filters.endDate}
                onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
                style={{ width: '140px' }}
              />
            </div>
          </Form.Group>
        </FilterGrid>
      </ReportParametersCard>
      </div>
      
      {/* Yellow Header Information */}
      <YellowHeaderInfo columnLabel="Employee Name" />
      
      {/* Report Data Section - Flexible */}
      <div style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0, // Important for flex child
        minWidth: 0
      }}>
        <Card style={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          flex: 1,
          minWidth: 0
        }}>
          <Card.Header className="tw-flex tw-justify-between tw-items-center" style={{ flexShrink: 0 }}>
          <h6 className="tw-mb-0">Report Data ({pagination.total} records)</h6>
          {pagination.total > 0 && (
            <Button
              variant="outline-primary"
              size="sm"
              onClick={() => setShowExportModal(true)}
              disabled={!canCreateCattleguard}
            >
              <i className="bi bi-download tw-mr-1"></i>
              Export Report
            </Button>
          )}
        </Card.Header>

          {/* Table Container - ReportPreviewTable owns its own scroll region (single mechanism,
              see ReportPreviewTable.tsx); no extra overflow wrapper needed here. */}
          <Card.Body className="tw-p-0" style={{
            flex: 1,
            minHeight: 0,
            minWidth: 0,
            display: 'flex',
            flexDirection: 'column'
          }}>
          <ReportPreviewTable
            columns={columns}
            data={previewData}
            pagination={pagination}
            loading={loadingPreview}
            emptyMessage="No cattleguard inventory found for these filters. Try All Service Areas."
            sortField={sortField}
            sortDirection={sortDirection}
            onSort={handleSort}
            isFiltersCollapsed={isFiltersCollapsed} // Pass collapse state to table
            yellowHeaderColumn="employeeName" // Make only Employee Name column yellow
          />
          </Card.Body>
        </Card>
      </div>

      <ExportOptionsModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        onPreliminaryExport={handleExport}
        onFinalExport={handleExportWithFilename}
        loadingPreliminary={loadingExport}
        loadingFinal={loadingExport}
        isCattleguard={true}
        showFilenameInput={true}
        defaultFilename={generateDefaultFilename()}
      />

      <Toast
        show={showToast}
        onClose={() => setShowToast(false)}
        message={toastMessage}
        type="success"
      />
    </div>
  );
};

export default CattleguardReportView;